'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { type ComponentType, type MouseEvent, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { type AppUpsellEntryPoint, buildAppDownloadUrl, isIPhone } from '@/config/app-upsells';
import { onEvent } from '@/events';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { appUpsellProperties, warnMissingAttributionLink } from '@/lib/app-upsells/analytics';
import { env } from '@/lib/config/env';
import type { AppDownloadDialogProps } from './app-download-dialog';
import { AppStoreLinks } from './app-store-links';
import { useUpsellImpression } from './use-upsell-impression';

const AppDownloadDialog: ComponentType<AppDownloadDialogProps> = dynamic(
  () => import('./app-download-dialog').then((module) => module.AppDownloadDialog),
  // Keep the first lazy mount inside this boundary, not the route's loading fallback.
  { loading: () => null },
);

interface AppDownloadActionProps {
  surface: 'feed' | 'navigation' | 'portfolio';
  entryPoint?: AppUpsellEntryPoint;
  onAfterAction?: (() => void) | undefined;
}

export function AppDownloadAction(props: AppDownloadActionProps): React.ReactNode {
  const rolloutEnabled = useFeatureGate('app_upsells') === true;
  const enabled = props.surface === 'navigation' || props.surface === 'portfolio' || rolloutEnabled;
  return enabled ? <EnabledAppDownloadAction {...props} /> : null;
}

function EnabledAppDownloadAction({
  surface,
  entryPoint = surface,
  onAfterAction,
}: AppDownloadActionProps): React.ReactNode {
  const t = useTranslations('app_upsells');
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const ref = useUpsellImpression('navigation', surface === 'navigation', entryPoint);

  function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
    const modified =
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1;
    const direct =
      isIPhone(navigator.userAgent) ||
      (surface === 'navigation' && /Android/i.test(navigator.userAgent)) ||
      modified;
    if (surface !== 'navigation') {
      onEvent('app_upsell_clicked', {
        ...appUpsellProperties(surface, entryPoint),
        destination: direct ? 'app_store' : 'dialog',
      });
      warnMissingAttributionLink();
    }
    if (modified) return;
    if (direct) {
      if (surface !== 'navigation') onAfterAction?.();
      return;
    }
    event.preventDefault();
    triggerRef.current = event.currentTarget;
    setLoaded(true);
    setOpen(true);
  }

  return (
    <div
      ref={ref}
      className={
        surface === 'navigation'
          ? 'w-full'
          : surface === 'portfolio'
            ? 'w-full sm:w-auto'
            : 'shrink-0'
      }
    >
      {surface === 'navigation' ? (
        <AppStoreLinks
          surface="navigation"
          entryPoint={entryPoint}
          onClick={handleClick}
          onAfterAction={onAfterAction}
          className="px-3"
        />
      ) : (
        <AnimatedButton
          asChild
          variant="primary"
          size={surface === 'portfolio' ? 'default' : 'lg'}
          disableHoverScale
          className={surface === 'portfolio' ? 'w-full rounded-panel-row sm:w-auto' : undefined}
        >
          <a
            ref={triggerRef}
            href={buildAppDownloadUrl(env.NEXT_PUBLIC_APPSFLYER_ONELINK_URL, entryPoint, 'direct')}
            onClick={handleClick}
            onAuxClick={(event: MouseEvent<HTMLAnchorElement>): void => {
              if (event.button === 1) handleClick(event);
            }}
            data-testid={`app-download-${surface}`}
          >
            {surface === 'portfolio' ? t('portfolio_download') : t('get_app')}
          </a>
        </AnimatedButton>
      )}
      {loaded ? (
        <AppDownloadDialog
          open={open}
          onOpenChange={setOpen}
          entryPoint={entryPoint}
          returnFocusRef={triggerRef}
        />
      ) : null}
    </div>
  );
}
